/**
 * «Мозг» диалогового AI-агента (детерминированное ядро, v1).
 *
 * Здесь — извлечение ИНН/суммы из свободного текста и решение, что ответить
 * или когда запускать анализ. Логика вынесена из компонента и не имеет
 * побочных эффектов — её просто тестировать и заменить на LLM.
 *
 * Шов под будущий LLM: интерфейс AgentBackend. v1 — DeterministicAgent
 * (локально, без сети). Позже появится LlmAgent поверх /api/agent/chat с тем
 * же turn() — компонент менять не придётся.
 */
import { formatRubles } from "./math_helpers";

export type AgentMode = "analysis" | "broker";

export interface AgentContext {
  mode: AgentMode;
  inn: string | null;
  amount: number | null;
  days: number | null;          // срок гарантии (брокер)
  category: string | null;      // gos/comm/tax/customs (брокер)
  guaranteeType: string | null; // вид гарантии: bid/exec_no_adv/exec_adv/warranty/adv_return
  law: string | null;           // режим закупки: 44/223/615/275/185
  beneficiaryInn: string | null; // ИНН заказчика/бенефициара (регион для стоп-факторов)
  tenderNumber: string | null;  // РНТ — реестровый номер торгов (закрытые торги/поиск по торгам)
  okpd2: string | null;         // предмет закупки (ОКПД2)
  experienceDeclared: boolean | null; // декларация опыта исполнения
  principal: Record<string, any>; // overrides профиля принципала: opf/usn/months/contracts
  flags: Record<string, boolean>;  // условия закупки (брокер): антидемпинг, аванс…
  sort: string | null;          // ранжирование результата: expensive/cheap/kv/best
  limit: number | null;         // сколько банков показать (топ-N из результата)
  sourceFilter: string | null;  // фильтр по источнику: Барсфин/ВБЦ/Гособлако
  eligibleOnly: boolean;        // показывать только проходящие банки
  excludeBanks: string[];       // банки-исключения из текста («исключи Альфу и ВТБ») — фильтр показа по имени
  brokerAsked: boolean;         // показывали ли сводку распознанных параметров на подтверждение
  brokerRan: boolean;           // был ли уже показан результат подбора (для мгновенной смены вида)
  files: File[];     // накопленные вложения для расчёта «по своим данным»
  analyzed: boolean;
}

export type AgentAction =
  | { type: "reply"; text: string }
  | { type: "analyze"; inn: string; amount: number; files: File[] }
  | { type: "broker"; amount: number; days: number; category: string;
      guaranteeType: string | null; law: string | null; inn: string | null;
      beneficiaryInn: string | null; tenderNumber: string | null;
      okpd2: string | null; experienceDeclared: boolean | null;
      principal: Record<string, any>;
      flags: Record<string, boolean>;
      sort: string | null; limit: number | null;
      sourceFilter: string | null; eligibleOnly: boolean; excludeBanks: string[] };

export interface AgentResponse {
  ctx: AgentContext;
  actions: AgentAction[];
}

export interface AgentBackend {
  greeting(mode: AgentMode): string;
  turn(userText: string, files: File[], ctx: AgentContext): Promise<AgentResponse>;
}

export const DEFAULT_LIMIT = 10_000_000;

export const initialContext = (mode: AgentMode = "analysis"): AgentContext => ({
  mode, inn: null, amount: null, days: null, category: null,
  guaranteeType: null, law: null, beneficiaryInn: null, tenderNumber: null,
  okpd2: null, experienceDeclared: null, principal: {},
  flags: {}, sort: null, limit: null, sourceFilter: null, eligibleOnly: false, excludeBanks: [],
  brokerAsked: false, brokerRan: false, files: [], analyzed: false,
});

/** Первый 10-значный ИНН ЮЛ, не входящий в более длинную цепочку цифр. */
export function extractInn(text: string): string | null {
  const m = (text || "").match(/(?:^|\D)(\d{10})(?:\D|$)/);
  return m ? m[1] : null;
}

/** Есть ли в тексте 12-значный ИНН (ИП/физлицо) — чтобы мягко подсказать. */
export function hasTwelveDigit(text: string): boolean {
  return /(?:^|\D)\d{12}(?:\D|$)/.test(text || "");
}

/** ИНН заказчика/бенефициара, если он явно помечен в тексте (10 цифр). */
export function extractBeneficiaryInn(text: string): string | null {
  const m = (text || "").match(/(?:заказчик|бенефициар|бенефит|получател)\w*[^0-9]{0,24}(\d{10})(?:\D|$)/i);
  return m ? m[1] : null;
}

const SCALE: { re: RegExp; mult: number }[] = [
  { re: /млрд|миллиард/i, mult: 1e9 },
  { re: /млн|миллион/i, mult: 1e6 },
  { re: /тыс|тысяч/i, mult: 1e3 },
];

/**
 * Сумма из текста: «50 млн», «2,5 млрд», «300 тыс», «5 000 000», «5000000».
 * `strip` (обычно уже распознанный ИНН) вырезается, чтобы 10 цифр ИНН не
 * приняли за сумму. Голое число суммой считаем только в диапазоне 4–9 цифр.
 */
export function extractAmount(text: string, strip?: string | null): number | null {
  let t = ` ${text || ""} `;
  if (strip) t = t.split(strip).join(" ");

  const ms = t.match(/(\d+(?:[.,]\d+)?)\s*(млрд|миллиард|млн|миллион|тыс\.?|тысяч)/i);
  if (ms) {
    const base = parseFloat(ms[1].replace(",", "."));
    const sc = SCALE.find((s) => s.re.test(ms[2]));
    if (sc && !Number.isNaN(base)) return Math.round(base * sc.mult);
  }
  const mg = t.match(/\d{1,3}(?:[  ]\d{3})+/);
  if (mg) return parseInt(mg[0].replace(/[  ]/g, ""), 10);
  const mb = t.match(/(?:^|\D)(\d{4,9})(?:\D|$)/);
  if (mb) return parseInt(mb[1], 10);
  return null;
}

/** Срок гарантии из текста: «365 дней», «1860 дн», «12 мес», «1 год», «3 года». */
export function extractDays(text: string): number | null {
  const t = ` ${text || ""} `;
  let m = t.match(/(\d{1,4})\s*(дн|дней|дня)/i);
  if (m) return parseInt(m[1], 10);
  m = t.match(/(\d{1,3})\s*мес/i);
  if (m) return parseInt(m[1], 10) * 30;
  m = t.match(/(\d{1,2})\s*(год|года|лет)/i);
  if (m) return parseInt(m[1], 10) * 365;
  return null;
}

/** Категория закупки из текста → ключ тарифа банка (gos/comm/tax/customs). */
export function extractCategory(text: string): string | null {
  const t = (text || "").toLowerCase();
  if (/коммерч|кбг/.test(t)) return "comm";
  if (/тамож/.test(t)) return "customs";
  if (/налог/.test(t)) return "tax";
  if (/44|223|615|185|275|гос|закуп|тендер|фз/.test(t)) return "gos";
  return null;
}

const CAT_LABEL: Record<string, string> = {
  gos: "госзакупки (44/223)", comm: "коммерческая", tax: "налоговая", customs: "таможенная",
};

const GT_LABEL: Record<string, string> = {
  bid: "обеспечение заявки", exec_no_adv: "исполнение (без аванса)",
  exec_adv: "исполнение (с авансом)", warranty: "гарантийные обязательства",
  adv_return: "возврат аванса",
};

/** Вид гарантии из текста → ключ (bid/exec_no_adv/exec_adv/warranty/adv_return). */
export function extractGuaranteeType(text: string): string | null {
  const t = (text || "").toLowerCase();
  if (/возврат[а-яё]*\s+аванс|аванс[а-яё]*\s+возврат/.test(t)) return "adv_return";
  if (/гарантийн[а-яё]*\s*(обязат|период)|на\s+гарантийн|гарантийного\s+период|гарантия\s+качеств|качеств[а-яё]*\s+работ/.test(t)) return "warranty";
  if (/исполнен/.test(t)) return /аванс/.test(t) ? "exec_adv" : "exec_no_adv";
  if (/обеспечен[а-яё]*\s+заявк|заявк|тендерн[а-яё]*\s+гарант|конкурсн[а-яё]*\s+гарант|обеспечение\s+участи/.test(t)) return "bid";
  return null;
}

const LAW_LABEL: Record<string, string> = {
  "44": "44-ФЗ", "223": "223-ФЗ", "615": "615-ПП (капремонт)",
  "275": "275-ФЗ (ГОЗ)", "185": "185-ФЗ",
};

/** Режим закупки → ключ закона (44/223/615/275/185). Требует контекст «ФЗ/ПП/по»,
 * чтобы «44 млн» не приняли за 44-ФЗ. */
export function extractLaw(text: string): string | null {
  const t = ` ${(text || "").toLowerCase()} `;
  if (/275[-\s]?фз|(?:^|[^а-яё])гоз(?:[^а-яё]|$)|оборонзаказ|оборонный\s+заказ/.test(t)) return "275";
  if (/615[-\s]?пп|капремонт|капитальн[а-яё]*\s+ремонт/.test(t)) return "615";
  if (/185[-\s]?фз/.test(t)) return "185";
  if (/223[-\s]?фз|по\s*223(?:\D|$)/.test(t)) return "223";
  if (/44[-\s]?фз|по\s*44(?:\D|$)/.test(t)) return "44";
  return null;
}

/** ОКПД2 — предмет закупки: помеченный «ОКПД2 45.2» либо код xx.xx(.xx…). */
export function extractOkpd2(text: string): string | null {
  const m = (text || "").match(/окпд\s*2?\s*[:№-]?\s*(\d{2}(?:\.\d{1,2}){0,4})/i);
  return m ? m[1] : null;
}

/** Декларация опыта исполнения: с опытом → true, без опыта → false, иначе null. */
export function extractExperience(text: string): boolean | null {
  const t = (text || "").toLowerCase();
  if (/без\s+опыт|нет\s+опыт|опыта\s+нет|отсутств[а-яё]*\s+опыт/.test(t)) return false;
  if (/с\s+опыт|есть\s+опыт|опыт\s+исполнен|наличие\s+опыт|опытн[а-яё]+\s+исполнител|по\s+(?:[а-яё]+\s+){0,2}опыт|учит[а-яё]*\s+опыт|мой\s+опыт|с\s+учёт[а-яё]*\s+опыт/.test(t)) return true;
  return null;
}

/** ОПФ принципала из текста → opf (ooo/ao/ip). */
export function extractOpf(text: string): string | null {
  const t = ` ${(text || "").toLowerCase()} `;
  if (/(?:^|[^а-яё])ип(?:[^а-яё]|$)|индивидуальн[а-яё]*\s+предприниматель/.test(t)) return "ip";
  if (/(?:^|[^а-яё])(?:пао|ао)(?:[^а-яё]|$)|акционерн/.test(t)) return "ao";
  if (/(?:^|[^а-яё])ооо(?:[^а-яё]|$)|обществ[а-яё]*\s+с\s+ограничен/.test(t)) return "ooo";
  return null;
}

/** Упомянута ли упрощённая система налогообложения (УСН). */
export function extractUsn(text: string): boolean {
  return /(?:^|[^а-яё])усн(?:[^а-яё]|$)|упрощён|упрощен|упрощенк/.test(` ${(text || "").toLowerCase()} `);
}

/** Флаги-условия закупки из свободного текста → ключи надбавок банка. */
export function extractFlags(text: string): Record<string, boolean> {
  const t = (text || "").toLowerCase();
  const f: Record<string, boolean> = {};
  if (/антидемп/.test(t)) f.antidemping = true;
  if (/валют/.test(t)) f.currency = true;
  if (/строит|стройк|строй/.test(t)) f.construction = true;
  if (/гоз|275|оборонзаказ|оборонный заказ/.test(t)) f.goz_275 = true;
  if (/концесс|(^|\D)115(\D|$)/.test(t)) f.concession_115 = true;
  if (/505/.test(t)) f.advance_505 = true;
  if (/(без|нет)[^.]{0,14}(еис|размещ|номер)/.test(t)) f.no_eis = true;
  if (/сниж/.test(t) && /50/.test(t)) f.nmck_drop_50 = true;
  return f;
}

export const FLAG_LABEL: Record<string, string> = {
  antidemping: "антидемпинг", currency: "валютная БГ", construction: "строительство",
  goz_275: "ГОЗ (275-ФЗ)", concession_115: "концессия (115-ФЗ)",
  advance_505: "аванс (505-ПП)", no_eis: "нет номера в ЕИС", nmck_drop_50: "снижение >50% НМЦК",
};

const RU_NUM: Record<string, number> = {
  "один": 1, "одну": 1, "два": 2, "две": 2, "три": 3, "четыре": 4, "пять": 5,
  "шесть": 6, "семь": 7, "восемь": 8, "девять": 9, "десять": 10,
};

/** РНТ — реестровый номер торгов (19 цифр ЕИС или помеченный «РНТ/реестровый номер»). */
export function extractTenderNumber(text: string): string | null {
  const t = text || "";
  let m = t.match(/(?:рнт|реестров[а-яё]*\s*(?:номер|№)?|извещени[а-яё]*\s*№?|закупк[а-яё]*\s*№|торг[а-яё]*\s*№)\s*[:№]?\s*(\d{11,25})/i);
  if (m) return m[1];
  m = t.match(/(?:^|\D)(\d{19})(?:\D|$)/);   // одиночный 19-значный номер ЕИС
  return m ? m[1] : null;
}

export const SORT_LABEL: Record<string, string> = {
  expensive: "самые дорогие (комиссия ↓)", cheap: "самые выгодные (комиссия ↑)",
  kv: "максимальный КВ агента", best: "рекомендуемые (проходят · КВ ↓)",
};

/** Ранжирование результата из текста: expensive/cheap/kv/best. */
export function extractSort(text: string): string | null {
  const t = (text || "").toLowerCase();
  if (/дорог|дороже|подорож|максимальн[а-яё]*\s+комисси|больш[а-яё]*\s+комисси|высок[а-яё]*\s+комисси/.test(t)) return "expensive";
  if (/дешев|дешёв|подешевл|выгодн|минимальн[а-яё]*\s+комисси|ниже\s+комисси|низк[а-яё]*\s+комисси/.test(t)) return "cheap";
  if (/(?:макс|больш|выш|высок|максимальн|наибольш)[а-яё]*\s+кв|кв\s+(?:макс|больш|выш)|по\s+кв(?![а-яё])|максимальн[а-яё]*\s+вознагражд|больш[а-яё]*\s+вознагражд/.test(t)) return "kv";
  if (/лучш|рекоменд|посоветуй|оптимальн|какие\s+(?:банки\s+)?выбрать|что\s+выбрать/.test(t)) return "best";
  return null;
}

/** Топ-N из результата: «5 самых…», «топ-5», «три банка», «первые 10». */
export function extractLimit(text: string): number | null {
  const t = (text || "").toLowerCase();
  let m = t.match(/(?:топ|top|перв[а-яё]+|показать|покажи|дай|перечисли|вывед[а-яё]+)[\s-]*(\d{1,2})/);
  if (m) return Math.min(50, Math.max(1, parseInt(m[1], 10)));
  m = t.match(/(\d{1,2})\s*(?:самы[а-яё]+|банк[а-яё]*|предложени[а-яё]*|вариант[а-яё]*|шт)/);
  if (m) return Math.min(50, Math.max(1, parseInt(m[1], 10)));
  for (const w in RU_NUM) {
    if (new RegExp(`${w}\\s+(?:самы[а-яё]+|банк|банка|банков|предложени|вариант)`).test(t)) return RU_NUM[w];
  }
  return null;
}

/** Ограничение по источнику данных: Барсфин/ВБЦ/Гособлако. */
export function extractSourceFilter(text: string): string | null {
  const t = (text || "").toLowerCase();
  if (/барсфин|барс\s?фин/.test(t)) return "Барсфин";
  if (/вбц/.test(t)) return "ВБЦ";
  if (/гособлак/.test(t)) return "Гособлако";
  return null;
}

/** «Только проходящие» — показать лишь офферы, прошедшие стоп-факторы. */
export function extractEligibleOnly(text: string): boolean {
  return /только\s+проходящ|котор[а-яё]+\s+проход|лишь\s+проходящ|подходящ[а-яё]+\s+банк|без\s+отказ|прошедш[а-яё]+\s+стоп/.test((text || "").toLowerCase());
}

// Слова, которые после «исключи/кроме» не являются именем банка.
const EXCL_STOP = new Set(["и", "а", "также", "ещё", "еще", "банк", "банки", "банков", "банка", "из", "список", "списка"]);

/** Банки-исключения из текста: «исключи Альфу и ВТБ», «кроме Сбера», «убери ВТБ» →
 * список стем-подстрок имён (одно окончание обрезаем — грубая нормализация склонений). */
export function extractExcludeBanks(text: string): string[] {
  const m = (text || "").toLowerCase().match(/(?:исключ[а-яё]*|кроме|убер[а-яё]*|не\s+показывай|минус)\s+(.+)$/);
  if (!m) return [];
  return m[1].split(/[\s,;]+/)
    .map((w) => w.replace(/[^а-яёa-z0-9-]/gi, ""))
    .filter((w) => w.length >= 3 && !EXCL_STOP.has(w))
    .map((w) => (w.length > 4 ? w.slice(0, w.length - 1) : w))   // «Альфу» → «Альф», «Сбера» → «Сбер»
    .slice(0, 6);
}

/** Возраст компании в месяцах (только с явным контекстом «компания/на рынке/возраст»). */
export function extractCompanyMonths(text: string): number | null {
  const t = (text || "").toLowerCase();
  let m = t.match(/(?:компани[а-яё]*|фирм[а-яё]*|на\s+рынке|возраст[а-яё]*|работает|существует)[^0-9]{0,16}(\d{1,3})\s*(год[а-яё]*|лет|мес[а-яё]*)/);
  if (!m) m = t.match(/(\d{1,3})\s*(год[а-яё]*|лет|мес[а-яё]*)\s*(?:на\s+рынке|компани|бизнес[а-яё]*|деятельност)/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return /мес/.test(m[2]) ? n : n * 12;
}

/** Число исполненных/завершённых контрактов (с контекстом «контракт»). */
export function extractContractsDone(text: string): number | null {
  const m = (text || "").toLowerCase().match(/(\d{1,4})\s*(?:[а-яё]+\s+){0,2}(?:госконтракт|контракт)/);
  return m ? parseInt(m[1], 10) : null;
}

/** Запрос показать анализ клиента (а не подбор): «дай аналитику по клиенту»,
 * «анализ клиента», «что по клиенту», «риск клиента»… */
export function isClientAnalyticsIntent(text: string): boolean {
  const t = (text || "").toLowerCase();
  return /аналитик|анализ\s+клиент|разбор\s+клиент|что\s+(?:по|с)\s+клиент|оцен[а-яё]*\s+клиент|риск[а-яё]*\s+клиент|покажи\s+анализ|клиент[а-яё]*\s+анализ|надёжн[а-яё]*\s+клиент/.test(t);
}

/** Запрос показать банки с проблемами (которые не проходят / стоп-факторы). */
export function isProblemBanksIntent(text: string): boolean {
  const t = (text || "").toLowerCase();
  return /проблемн|с\s+проблем|где\s+проблем|не\s+проход|котор[а-яё]+\s+не\s+про|почему\s+не\s+про|стоп[\s-]?фактор|не\s+подход[а-яё]*\s+банк|провал|отказн/.test(t);
}

/** Запрос пересчитать по отчётности клиента (потребуется файл). */
export function isReportRecalcIntent(text: string): boolean {
  const t = (text || "").toLowerCase();
  return /по\s+(?:мо[ейю][а-яё]*\s+)?отч[её]тност|пересчит[а-яё]*\s+(?:по\s+)?(?:мо[ейю][а-яё]*\s+)?отч[её]т|по\s+балансу|по\s+бухотч/.test(t);
}

/** Человекочитаемая сводка распознанных параметров («фактические кнопки» разделов
 * «Банки»/«Анализ») — показываем пользователю на подтверждение перед запуском. */
export function brokerSummaryLines(ctx: AgentContext): string[] {
  const L: string[] = [];
  if (ctx.guaranteeType) L.push(`• Вид гарантии: ${GT_LABEL[ctx.guaranteeType] || ctx.guaranteeType}`);
  if (ctx.law) L.push(`• Закон: ${LAW_LABEL[ctx.law] || ctx.law}`);
  L.push(`• Категория: ${CAT_LABEL[ctx.category ?? "gos"] || ctx.category}`);
  if (ctx.amount != null) L.push(`• Сумма: ${formatRubles(ctx.amount)}`);
  if (ctx.days != null) L.push(`• Срок: ${ctx.days} дн.`);
  const conds = Object.keys(ctx.flags).filter((k) => ctx.flags[k]).map((k) => FLAG_LABEL[k] || k);
  if (conds.length) L.push(`• Условия: ${conds.join(", ")}`);
  if (ctx.okpd2) L.push(`• ОКПД2: ${ctx.okpd2}`);
  if (ctx.tenderNumber) L.push(`• РНТ (реестровый номер торгов): ${ctx.tenderNumber}`);
  if (ctx.inn) L.push(`• ИНН принципала: ${ctx.inn}`);
  if (ctx.beneficiaryInn) L.push(`• ИНН заказчика: ${ctx.beneficiaryInn}`);
  if (ctx.experienceDeclared != null) L.push(`• Опыт исполнения: ${ctx.experienceDeclared ? "есть" : "нет"}`);
  if (ctx.principal.opf) L.push(`• ОПФ: ${String(ctx.principal.opf).toUpperCase()}`);
  if (ctx.principal.usn) L.push("• Налоги: УСН");
  if (ctx.principal.months) L.push(`• Возраст компании: ${ctx.principal.months} мес.`);
  if (ctx.principal.contracts != null) L.push(`• Исполненных контрактов: ${ctx.principal.contracts}`);
  if (ctx.sort) L.push(`• Ранжирование: ${SORT_LABEL[ctx.sort] || ctx.sort}`);
  if (ctx.limit) L.push(`• Показать: топ-${ctx.limit}`);
  if (ctx.sourceFilter) L.push(`• Источник: ${ctx.sourceFilter}`);
  if (ctx.eligibleOnly) L.push("• Только проходящие банки");
  if (ctx.excludeBanks.length) L.push(`• Исключить банки: ${ctx.excludeBanks.join(", ")}`);
  return L;
}

// Глаголы-намерения «посчитать/подобрать» — ищем подстрокой (без \b: он не
// срабатывает после кириллицы, т.к. кириллица не входит в \w в JS-regex).
const VERB_RE = /(анализ|посчита|рассчита|расч[её]т|проверь|оцен|запуст|сдела|подбер|подобрать|подбор|выбер|выбор|отбер|отбор|покаж|перечисл|подскаж|найди|найти)/i;
// Короткое утвердительное сообщение целиком («да», «давай», «ок», «поехали»…).
const AFFIRM_RE = /^(да|ага|угу|ок|окей|давай|поехали|погнали|готов|впер[её]д|плюс|конечно|начинай|считай|жми)[!.\s]*$/i;

/** Детерминированный агент: сценарий сбор ИНН/суммы/файлов → анализ. */
export class DeterministicAgent implements AgentBackend {
  greeting(mode: AgentMode = "analysis"): string {
    if (mode === "broker") {
      return (
        "Режим «Брокер». Опишите задачу словами — разберу её в параметры подбора и покажу " +
        "на подтверждение (запущу только после «да»). Понимаю: вид гарантии (заявка / " +
        "исполнение / возврат аванса / гарантийные обязательства), закон (44-ФЗ, 223-ФЗ, " +
        "615-ПП, 275-ФЗ), сумму («5 млн»), срок («365 дней», «1 год»), условия (аванс, " +
        "антидемпинг, строительство, валюта, ГОЗ…), ИНН принципала и заказчика, РНТ (реестровый " +
        "номер торгов — подтяну сумму обеспечения/закон/заказчика/ОКПД2), ОКПД2, опыт, ОПФ/УСН. " +
        "Плюс фильтры результата: «5 самых дорогих», «самые выгодные», «максимальный КВ», " +
        "«только проходящие», источник (Барсфин/ВБЦ/Гособлако). Пример: «Гарантия на исполнение " +
        "по 44-ФЗ, 5 млн, 1 год, строительство, ИНН 7707083893, 5 самых дорогих». " +
        "Вопрос вне этого списка — отвечу сам по данным ANYskin (реестр банков, методики, " +
        "тендеры, ваши подборы)."
      );
    }
    return (
      "Здравствуйте! Я — AI-агент ANYskin. Пришлите ИНН контрагента (10 цифр). " +
      "По желанию укажите сумму кредита (например, «50 млн») и прикрепите отчётность " +
      "(PDF/Excel/скан) — тогда расчёт пойдёт по вашим данным. Проведу экспресс-анализ " +
      "и отвечу сводкой с таблицами. Спросить можно и свободным текстом — отвечу по данным " +
      "ANYskin (карточка компании, методики, банки)."
    );
  }

  async turn(userText: string, files: File[], prev: AgentContext): Promise<AgentResponse> {
    const ctx: AgentContext = { ...prev, files: [...prev.files, ...files] };
    const text = (userText || "").trim();

    const inn = extractInn(text);
    if (inn) ctx.inn = inn;

    if (ctx.mode === "broker") return this.turnBroker(text, ctx);

    const amount = extractAmount(text, inn ?? ctx.inn);
    if (amount != null) ctx.amount = amount;

    const actions: AgentAction[] = [];
    const wantsAnalyze = VERB_RE.test(text) || AFFIRM_RE.test(text) || (files.length > 0 && !!ctx.inn);

    if (!ctx.inn) {
      actions.push({
        type: "reply",
        text: hasTwelveDigit(text)
          ? "Вижу 12-значный ИНН — это индивидуальный предприниматель. Оценка по нему выполняется только по годовой отчётности: приложите её на экране анализа, и я продолжу."
          : "Не вижу ИНН. Пришлите ИНН: 10 цифр для юрлица, 12 — для предпринимателя.",
      });
      return { ctx, actions };
    }

    if (wantsAnalyze) {
      actions.push({ type: "analyze", inn: ctx.inn, amount: ctx.amount ?? DEFAULT_LIMIT, files: ctx.files });
      return { ctx, actions };
    }

    const limit = formatRubles(ctx.amount ?? DEFAULT_LIMIT);
    const fileNote = ctx.files.length
      ? ` Прикреплено документов: ${ctx.files.length} — расчёт пойдёт по вашей отчётности.`
      : "";
    actions.push({
      type: "reply",
      text: `Принял ИНН ${ctx.inn}. Запустить анализ за лимит ${limit}?${fileNote} ` +
        "Можно уточнить сумму («50 млн») или прикрепить отчётность. Напишите «да» — и я посчитаю.",
    });
    return { ctx, actions };
  }

  /** Режим «Брокер»: разбираем текст в фактические параметры разделов «Банки»/«Анализ»
   * (вид/закон/категория/сумма/срок/условия/ИНН/РНТ/ОКПД2/опыт/ОПФ + фильтры результата),
   * показываем на подтверждение и запускаем подбор только после «да». */
  private turnBroker(text: string, ctx: AgentContext): AgentResponse {
    // РНТ (реестровый номер торгов) — вырезаем до суммы/ИНН, чтобы длинный номер не спутать.
    const rnt = extractTenderNumber(text);
    if (rnt) ctx.tenderNumber = rnt;
    let t = rnt ? text.split(rnt).join(" ") : text;

    // ИНН заказчика (если помечен) — до принципала.
    const benInn = extractBeneficiaryInn(t);
    if (benInn) {
      ctx.beneficiaryInn = benInn;
      if (ctx.inn === benInn) ctx.inn = extractInn(t.split(benInn).join(" "));
      t = t.split(benInn).join(" ");
    }

    const gt = extractGuaranteeType(text);
    if (gt) ctx.guaranteeType = gt;
    const law = extractLaw(text);
    if (law) { ctx.law = law; if (!ctx.category) ctx.category = "gos"; }
    const cat = extractCategory(text);
    if (cat) ctx.category = cat;
    const okpd = extractOkpd2(text);
    if (okpd) ctx.okpd2 = okpd;
    const exp = extractExperience(text);
    if (exp != null) ctx.experienceDeclared = exp;

    // Профиль принципала (overrides): возраст / контракты / ОПФ / УСН.
    const cMonths = extractCompanyMonths(text);
    if (cMonths != null) ctx.principal = { ...ctx.principal, months: cMonths };
    const cDone = extractContractsDone(text);
    if (cDone != null) ctx.principal = { ...ctx.principal, contracts: cDone };
    const opf = extractOpf(text);
    if (opf) ctx.principal = { ...ctx.principal, opf };
    const usnHit = extractUsn(text);
    if (usnHit) ctx.principal = { ...ctx.principal, usn: true };

    // Срок и сумму парсим из «очищенного» текста: убираем возраст/контракты (чтобы не
    // спутать с суммой), затем для суммы — ещё «дневные» и «законные» числа.
    const scrub = t
      .replace(/(?:компани[а-яё]*|фирм[а-яё]*|на\s+рынке|возраст[а-яё]*|работает|существует)[^0-9]{0,16}\d{1,3}\s*(?:год[а-яё]*|лет|мес[а-яё]*)/gi, " ")
      .replace(/\d{1,4}\s*(?:[а-яё]+\s+){0,2}(?:госконтракт|контракт)[а-яё]*/gi, " ");
    const days = extractDays(scrub);
    if (days != null) ctx.days = days;
    const amtText = scrub
      .replace(/\d{1,4}\s*(дн|дней|дня|мес\w*|год\w*|лет)/gi, " ")
      .replace(/(44|223|615|275|185)[-\s]?(фз|пп)/gi, " ");
    const amount = extractAmount(amtText, ctx.inn);
    if (amount != null) ctx.amount = amount;

    // Флаги-условия и предпочтения результата накапливаем из каждого сообщения.
    const flagsBefore = Object.keys(ctx.flags).filter((k) => ctx.flags[k]).length;
    ctx.flags = { ...ctx.flags, ...extractFlags(text) };
    const flagsChanged = Object.keys(ctx.flags).filter((k) => ctx.flags[k]).length !== flagsBefore;
    const sort = extractSort(text);
    if (sort) ctx.sort = sort;
    const limit = extractLimit(text);
    if (limit != null) ctx.limit = limit;
    const src = extractSourceFilter(text);
    if (src) ctx.sourceFilter = src;
    const eligHit = extractEligibleOnly(text);
    if (eligHit) ctx.eligibleOnly = true;
    const excl = extractExcludeBanks(text);
    if (excl.length) ctx.excludeBanks = [...new Set([...ctx.excludeBanks, ...excl])];

    const wantsRun = VERB_RE.test(text) || AFFIRM_RE.test(text);
    const actions: AgentAction[] = [];
    const reply = (msg: string) => { actions.push({ type: "reply", text: msg }); return { ctx, actions }; };

    // Без суммы/срока подбор невозможен — спрашиваем недостающее.
    if (ctx.amount == null)
      return reply("Укажите сумму гарантии — например, «5 млн» или «3 000 000». Или пришлите РНТ (реестровый номер торгов) — подтяну сумму обеспечения из тендера.");
    if (ctx.days == null)
      return reply("На какой срок гарантия? Укажите в днях («365 дней») или словами («1 год», «6 месяцев»).");

    const run = () => {
      actions.push({
        type: "broker", amount: ctx.amount!, days: ctx.days!, category: ctx.category ?? "gos",
        guaranteeType: ctx.guaranteeType, law: ctx.law, inn: ctx.inn,
        beneficiaryInn: ctx.beneficiaryInn, tenderNumber: ctx.tenderNumber,
        okpd2: ctx.okpd2, experienceDeclared: ctx.experienceDeclared, principal: ctx.principal,
        flags: ctx.flags, sort: ctx.sort, limit: ctx.limit,
        sourceFilter: ctx.sourceFilter, eligibleOnly: ctx.eligibleOnly, excludeBanks: ctx.excludeBanks,
      });
      ctx.brokerAsked = false;
      ctx.brokerRan = true;
      return { ctx, actions };
    };

    // Явная команда-действие («выбери/подбери/покажи/да»…) при полных параметрах —
    // запускаем сразу, без повторных подтверждений (иначе диалог зацикливается).
    if (wantsRun) return run();

    // Смена только предпочтений показа (сортировка/лимит/фильтр/исключение) после уже
    // показанного подбора — пересобираем показ сразу (в компоненте — ре-сорт без запроса).
    const searchChanged = !!(gt || law || cat || okpd || exp != null || cMonths != null ||
      cDone != null || opf || usnHit || days != null || amount != null || benInn || rnt || flagsChanged);
    const viewIntent = !!(sort || limit != null || src || eligHit || excl.length);
    if (ctx.brokerRan && viewIntent && !searchChanged) return run();

    // Иначе (даны параметры без команды) — один раз показываем сводку на подтверждение.
    ctx.brokerAsked = true;
    const lines = brokerSummaryLines(ctx);
    return reply(
      "Разобрал запрос в параметры подбора:\n" + lines.join("\n") +
      "\n\nНапишите «да» / «подбери» (или нажмите «Подобрать гарантию»), либо уточните: " +
      "«на возврат аванса», «223-ФЗ», «срок 2 года», «сумма 10 млн», «5 самых дорогих», " +
      "«только проходящие», «источник ВБЦ», «исключи Альфу и ВТБ».",
    );
  }
}
